import Image from 'next/image';
import { useRouter } from 'next/router';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery } from 'react-query';
import 'swiper/css';
import 'swiper/css/pagination';
import { getAllFolder } from '../../apis/fake.api';
import { convertArrayOfObject } from '../../common/convert';
import { PATH_NAME } from '../../configs/pathName';
import { FOLDER_LIST } from '../../constant/QueryKeyName';
import {
  FOLDERS_PER_ROW,
  getMaxFolderRows,
} from '../../constant/numericParams';
import { IFolderPreviewGallery } from '../../interfaces/components/gallery.interface';
import FolderPreview from '../cards/FolderPreview.card';
import FolderPreviewSkeleton from '../elements/FolderPreviewSkeleton';
import BottomDrawer from '../elements/drawers/BottomDrawer';
import { RightClickClone } from '../menu/RightClick.menu';

const FolderPreviewGallery = ({
  groups = false,
  title,
  setCurrentItem,
  rightClick,
  handleClickItem,
  handleRightClickItem,
  loading: isLoading = false,
  isDeleted: _isDeleted = false,
  fetchDataFunc = getAllFolder,
  customReactQuerySelect = (data: any) => ({
    data: convertArrayOfObject(data.pages),
    meta: data.pages[0]?.totalCount || 0,
  }),
  queryKey = [FOLDER_LIST, FOLDERS_PER_ROW],
  // base on your fetchDataFunc, define queryKey suitable, see queryKey and fetchDataFunc above to see example
  handleFetchNextPage = (_lastPage: any, _allPages: any[]) => {
    return _lastPage?.hasNextPage ? _lastPage?.currentPage + 1 : undefined;
  },
  titleClassName = '',
  enable = true,
}: IFolderPreviewGallery) => {
  const {
    data,
    isLoading: isReactQueryLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(queryKey, fetchDataFunc, {
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    select: customReactQuerySelect,
    getNextPageParam: handleFetchNextPage,
    keepPreviousData: true,
    enabled: enable,
  });

  const gridRef = useRef<HTMLDivElement>(null);
  // ref for meta defined below, use ref because
  // event listener callback cannot access latest state
  const metaRef = useRef<number>(0);
  const [numFoldersShown, setNumFoldersShown] = useState(FOLDERS_PER_ROW);
  const [numFoldersPerRow, setNumFoldersPerRow] = useState(0);

  // update number of files shown every time we resize window
  useEffect(() => {
    window.addEventListener('resize', updateNumFoldersShown);
    return () => window.removeEventListener('resize', updateNumFoldersShown);
  }, []);

  // update number of files shown every time we fetch files
  useEffect(() => {
    updateNumFoldersShown();
  }, [data]);

  // calculate number of files shown/hidden
  const updateNumFoldersShown = () => {
    if (!gridRef.current) return;
    // newNumFoldersPerRow =  number of files that can be fitted into 1 row
    const computedRowString = window
      .getComputedStyle(gridRef.current as HTMLDivElement)
      .getPropertyValue('grid-template-columns');
    const newNumFoldersPerRow = computedRowString.split(' ').length;

    setNumFoldersPerRow(newNumFoldersPerRow);

    // numRowsShown = number of full rows that we have
    // minimum is 1 row
    const numRowsShown = Math.max(
      Math.floor(metaRef.current / newNumFoldersPerRow),
      1
    );

    // if num of full rows larger than 3, just display 3 rows
    if (numRowsShown > getMaxFolderRows(newNumFoldersPerRow)) {
      setNumFoldersShown(
        newNumFoldersPerRow * getMaxFolderRows(newNumFoldersPerRow)
      );
      return;
    }

    // else display numRowsShown rows
    setNumFoldersShown(newNumFoldersPerRow * numRowsShown);
  };

  // const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  const [activeItem, setActiveItem] = useState<any>();

  const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const item = useMemo(() => {
    const cloneData = data as any;
    return cloneData?.data || [];
  }, [data]);
  const meta = useMemo(() => {
    const cloneData = data as any;
    // update metaRef for event listener callback to use
    metaRef.current = cloneData?.meta || item.length;
    return cloneData?.meta || item.length;
  }, [data, item]);
  const router = useRouter();
  const { ref, inView } = useInView(); //detect if element in the viewport

  const handleOpenContextMenu = useCallback(
    (event, item) => {
      setCurrentItem && setCurrentItem(item);
      setActiveItem(item);
      event.preventDefault();
      setAnchorPoint({ x: event.pageX, y: event.pageY });
      setShowMenu(true);
    },
    [setCurrentItem]
  );

  const handleCloseContextMenu = useCallback(
    () => (showMenu ? setShowMenu(false) : null),
    [showMenu]
  );

  const handleClick = useCallback(
    (id: string) => {
      handleCloseContextMenu();
      groups
        ? router.push(`${PATH_NAME.MY_GROUPS}/${id}`)
        : router.push(`${PATH_NAME.FOLDER_DETAIL}/${id}`);
    },
    [groups, handleCloseContextMenu, router]
  );

  useEffect(() => {
    if (inView && !isFetchingNextPage && item.length < meta) fetchNextPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, item, meta]);

  useEffect(() => {
    document.addEventListener('click', handleCloseContextMenu);
    return () => {
      document.removeEventListener('click', handleCloseContextMenu);
    };
  });

  const notLoadingCondition =
    !isReactQueryLoading && !isFetching && !isFetchingNextPage;

  if (!meta && notLoadingCondition) return null;
  return (
    <div>
      <div className="mb-4 flex items-end gap-6">
        <span
          className={`${titleClassName} flex items-center rounded-lg rounded-tr-[50px] p-3 pr-10 text-lg font-semibold leading-none`}
        >
          {title}
        </span>
      </div>
      <div>
        {isReactQueryLoading || (
          <>
            <section>
              <div
                ref={gridRef}
                className="mb-4 grid grid-cols-1 gap-2 lg:grid-cols-3 xl:grid-cols-4"
              >
                {item?.length > 0 &&
                  [...item]
                    ?.slice(0, numFoldersShown)
                    .map((item: any, index: number) => (
                      <div
                        className="w-full"
                        key={index + item?.id}
                        onClick={() => handleClick(item?.id)}
                        onContextMenu={(e) => handleOpenContextMenu(e, item)}
                      >
                        <FolderPreview
                          handleClick={() => {
                            handleClickItem && handleClickItem(item);
                          }}
                          handleContextMenu={() => {
                            handleRightClickItem && handleRightClickItem(item);
                          }}
                          capacity={item?.numOfFiles}
                          numOfMember={item?.numOfMembers}
                          key={item?.id}
                          folderName={item?.folderName || item?.name}
                          dateLeft={
                            _isDeleted
                              ? item?.deletedOnRemainingDays
                              : undefined
                          }
                          handleSeeMore={() => {
                            setShowMenu(true);
                            setActiveItem(item);
                          }}
                          isDeleted={_isDeleted}
                          avatar={item?.avatar}
                        />
                      </div>
                    ))}

                {/* insert empty divs so that there's always at least
                  numFoldersPerRow columns (because we're using grid-autofit) */}
                {item.length < numFoldersPerRow &&
                  new Array(numFoldersPerRow - item?.length).fill(
                    <div className="h-0"></div>
                  )}

                {isExpanded &&
                  [...item]
                    .slice(numFoldersShown, item.length)
                    .map((item: any, index: number) => (
                      <div
                        className="w-full"
                        key={index + item?.id}
                        onContextMenu={(e) => handleOpenContextMenu(e, item)}
                        onClick={() => handleClick(item?.id)}
                      >
                        <FolderPreview
                          handleClick={() => {
                            handleClickItem && handleClickItem(item);
                          }}
                          handleContextMenu={() => {
                            handleRightClickItem && handleRightClickItem(item);
                          }}
                          capacity={item?.numOfFiles}
                          key={item?.id}
                          numOfMember={item?.numOfMembers}
                          classname="w-full"
                          folderName={item?.folderName || item?.name}
                          dateLeft={
                            _isDeleted
                              ? item?.deletedOnRemainingDays
                              : undefined
                          }
                          handleSeeMore={() => {
                            setShowMenu(true);
                            setActiveItem(item);
                          }}
                          isDeleted={_isDeleted}
                          avatar={item?.avatar}
                        />
                      </div>
                    ))}
              </div>

              {!isFetchingNextPage && item?.length < meta && (
                <div
                  ref={ref}
                  className="invisible h-[1px] w-full bg-transparent"
                ></div>
              )}
            </section>
            {item?.length === 0 && groups && (
              <div className="flex w-full flex-col justify-center gap-y-2 pt-10">
                <div className="col-span-2 flex justify-center">
                  <Image
                    width={232}
                    height={89}
                    src={`/img/group-empty.png`}
                    alt="group-empty"
                  />
                </div>
                <p className="text-center text-sm text-light_grey">
                  Bạn chưa tham gia vào nhóm nào.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {(isReactQueryLoading || isFetchingNextPage) && (
        <div className="mt-4">
          <FolderPreviewSkeleton />
        </div>
      )}

      {meta > numFoldersShown && (
        <div
          className="ml-auto mt-7 cursor-pointer leading-none text-slate_gray sm:ml-0"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? (
            <span className="flex items-center justify-center gap-1">
              <Image src="/icons/up-arrow.svg" width={15} height={15} /> Thu gọn
            </span>
          ) : (
            <span className="flex items-center justify-center gap-1">
              Xem tất cả ({meta})
            </span>
          )}
        </div>
      )}

      {showMenu && (
        <div
          className="absolute z-10 hidden lg:block"
          style={{ left: `${anchorPoint.x}px`, top: `${anchorPoint.y}px` }}
        >
          <RightClickClone activeItem={activeItem}>
            {rightClick ? rightClick : <></>}
          </RightClickClone>
        </div>
      )}
      <BottomDrawer
        isOpen={showMenu}
        handleClose={() => setShowMenu(false)}
        title="Mở rộng"
      >
        <RightClickClone activeItem={activeItem}>
          {rightClick ? rightClick : <></>}
        </RightClickClone>
      </BottomDrawer>
    </div>
  );
};

export default memo(FolderPreviewGallery);
